import React, { useEffect, useState } from "react";
import { CircleCheck, CloudDownload, Loader2, AlertTriangle, Ticket, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { idb } from "@/lib/offlineDb";
import { READY_KEY, STATS_KEY, prefetchProject } from "@/lib/offlinePrefetch";
import MissingPhotosDialog from "./MissingPhotosDialog";

function useCacheEntry(key) {
  const [val, setVal] = useState({});
  useEffect(() => {
    const load = () => idb.get("cache", key).then((r) => setVal((r && r.data) || {})).catch(() => {});
    const onState = (e) => { if (e.detail?.finished) load(); };
    load();
    window.addEventListener("prefetch-state", onState);
    window.addEventListener("offline-ready-changed", load);
    return () => {
      window.removeEventListener("prefetch-state", onState);
      window.removeEventListener("offline-ready-changed", load);
    };
  }, [key]);
  return val;
}

// projectId -> ready timestamp, refreshed when the offline prefetch finishes.
export const useOfflineReadyProjects = () => useCacheEntry(READY_KEY);

// Offline-Prüfliste: what of this project is stored on this device.
export function OfflineCounts({ projectId, testid }) {
  const s = useCacheEntry(STATS_KEY)[projectId] || { defects: 0, tickets: 0, photos: 0 };
  const [showMissing, setShowMissing] = useState(false);
  const t = s.total || {};
  const items = [[AlertTriangle, s.defects, "Mängel", "defects"], [Ticket, s.tickets, "Tickets", "tickets"], [ImageIcon, s.photos, "Fotos", "photos"]];
  return (
    <span data-testid={testid || `project-offline-counts-${projectId}`} title="Auf diesem Gerät offline gespeichert – von Gesamtzahl auf dem Server"
      className="inline-flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-slate-500">
      {items.map(([Icon, n, label, k]) => {
        const total = Math.max(t[k] ?? n, n);
        const inner = <><Icon className="h-3.5 w-3.5" /><b className={`font-semibold ${n < total ? "text-amber-700" : "text-slate-700"}`}>{n}</b> von {total} {label}</>;
        if (k === "photos" && n < total) {
          return (
            <button key={k} type="button" data-testid={`offline-count-${k}-${projectId}`} title="Fehlende Fotos anzeigen und nachladen"
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); setShowMissing(true); }}
              className="inline-flex items-center gap-1 text-amber-700 underline decoration-dotted underline-offset-2 hover:text-amber-800">
              {inner}
            </button>
          );
        }
        return (
          <span key={k} className={`inline-flex items-center gap-1 ${n < total ? "text-amber-700" : ""}`} data-testid={`offline-count-${k}-${projectId}`}>
            {inner}
          </span>
        );
      })}
      {showMissing && <MissingPhotosDialog open onClose={() => setShowMissing(false)} missing={s.missing || []} />}
    </span>
  );
}

// Offline-Check vor Ort: short hint when the device is offline and photos of this project are not stored.
export function OfflineMissingHint({ projectId }) {
  const missing = (useCacheEntry(STATS_KEY)[projectId]?.missing || []).length;
  const [offline, setOffline] = useState(!navigator.onLine);
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  if (!offline || !missing || hidden) return null;
  return (
    <div role="status" data-testid="project-offline-missing-hint"
      className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 animate-in fade-in slide-in-from-top-1">
      <ImageIcon className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
      <p className="flex-1">
        <b>{missing === 1 ? "1 Foto fehlt" : `${missing} Fotos fehlen`}</b> auf diesem Gerät und {missing === 1 ? "ist" : "sind"} ohne Internet nicht sichtbar.
        Mit Verbindung auf „von … Fotos“ tippen und nachladen.
      </p>
      <button type="button" onClick={() => setHidden(true)} aria-label="Hinweis schließen" data-testid="project-offline-missing-hint-close"
        className="text-amber-700 hover:text-amber-900 text-lg leading-none px-1">×</button>
    </div>
  );
}

export function ReloadButton({ projectId, testid, load, okMsg = "Projekt ist jetzt offline verfügbar", onDone, title = "Dieses Projekt erneut für offline laden" }) {
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e.stopPropagation();
    if (!navigator.onLine) return toast.error("Nachladen geht nur mit Internetverbindung");
    setBusy(true);
    const ok = await (load ? load() : prefetchProject(projectId)).catch(() => false);
    setBusy(false);
    onDone && onDone(ok);
    if (ok) toast.success(okMsg);
    else toast.error("Nicht alles konnte geladen werden – bitte erneut versuchen");
  };
  return (
    <button type="button" onClick={run} disabled={busy} data-testid={testid || `project-offline-reload-${projectId}`}
      title={title}
      className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600 hover:border-primary hover:text-primary transition-colors disabled:opacity-60">
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CloudDownload className="h-3.5 w-3.5" />}
      {busy ? "Lädt …" : "Offline nachladen"}
    </button>
  );
}

export default function OfflineReadyBadge({ projectId, ready, testid }) {
  const ts = ready[projectId];
  if (!ts) {
    return (
      <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
        <ReloadButton projectId={projectId} />
        <OfflineCounts projectId={projectId} testid={testid ? `${testid}-counts` : undefined} />
      </span>
    );
  }
  const when = new Date(ts).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  return (
    <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
      <span data-testid={testid || `project-offline-ready-${projectId}`} title={`Komplett offline verfügbar (Stand ${when})`}
        className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
        <CircleCheck className="h-3.5 w-3.5" />Offline verfügbar
      </span>
      <OfflineCounts projectId={projectId} testid={testid ? `${testid}-counts` : undefined} />
    </span>
  );
}
